import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/Button';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import { QRCodeEditForm, EDITOR_SECTIONS, EditorSectionId } from '../../components/qr/QRCodeEditForm';
import { qrApi } from '../../api/services/qr.service';
import { QRCodeDetailResponse } from '../../types/qr.types';

function sectionFromHash(hash: string): EditorSectionId | undefined {
  const id = hash.replace(/^#/, '');
  return EDITOR_SECTIONS.find((s) => s.id === id)?.id;
}

export default function QRCodeEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const qrIdentifier = id || '';
  // Native beforeunload (tab close/refresh) can't go through React context, so this
  // page still tracks its own dirty ref for that. In-app navigation (the form's Back
  // button, DashboardLayout's sidebar) is guarded via UnsavedChangesContext instead —
  // see QRCodeEditForm, which is rendered inside DashboardLayout and so can consume it.
  const isDirtyRef = useRef(false);
  const [isDirty, setIsDirty] = useState(false);

  const { data: qrCode, isLoading } = useQuery({
    queryKey: ['qr-code', qrIdentifier],
    // @ts-ignore - API accepts both string and number
    queryFn: () => qrApi.getQRCode(qrIdentifier),
    enabled: !!qrIdentifier,
  });

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const goBack = () => navigate(`/dashboard/qr-codes/${qrIdentifier}`);

  if (isLoading) {
    return (
      <DashboardLayout currentPage="qr-codes">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!qrCode) {
    return (
      <DashboardLayout currentPage="qr-codes">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <p className="text-gray-600 mb-4">QR code not found</p>
          <Button onClick={() => navigate('/dashboard/qr-codes')}>Back to QR Codes</Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="qr-codes">
      <QRCodeEditForm
        initialData={qrCode as QRCodeDetailResponse}
        initialSection={sectionFromHash(location.hash)}
        onBack={goBack}
        onDirtyChange={setIsDirty}
        onSuccess={() => {
          setIsDirty(false);
          queryClient.invalidateQueries({ queryKey: ['qr-code', qrIdentifier] });
          queryClient.invalidateQueries({ queryKey: ['qr-analytics-summary', qrIdentifier] });
          navigate(`/dashboard/qr-codes/${qrIdentifier}`);
        }}
      />
    </DashboardLayout>
  );
}
